// @ts-nocheck
/* Base File Viewer dialog. Fetches campaign numbers page by page via
 * API_VOICE_CAMPAIGNS_NUMBERS (one base at a time), supports "go-to line"
 * with row highlight, warning banners, source-label footer. */
import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiCall } from '@/service/httpCall';
import { API_VOICE_CAMPAIGNS_NUMBERS } from '@/service/url';
import { canDownloadReports } from '@/utilities/promptUploadAccess';
import { downloadCsv } from '@/components/data-table/csv-export';
import { fetchAuthedBlobUrl, revokeAuthedBlobUrl } from '@/utilities/mediaAudio';

const CAMPAIGN_NUMBER_UPLOAD_SAMPLE = 'Number\n9876543210\n9123456789\n';

export function downloadNumberTemplate() {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([CAMPAIGN_NUMBER_UPLOAD_SAMPLE], { type: 'text/csv' }));
  a.download = 'Campaign-Numbers-Upload.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function downloadBaseFile(b, campaignId) {
  if (b?.contact_group_id && campaignId) {
    try {
      const res: any = await getApiCall(API_VOICE_CAMPAIGNS_NUMBERS.replace(':id', String(campaignId)), {
        base_id: b.base_id,
        with_fields: 1,
      });
      downloadCsv([res.columns, ...res.rows], b.base_name);
    } catch {
    }
    return;
  }
  if (!b?.download_url) return;
  try {
    const { blob, objectUrl } = await fetchAuthedBlobUrl(b.download_url);
    revokeAuthedBlobUrl(objectUrl);
    const text = await blob.text();
    const url = URL.createObjectURL(new Blob([/^\s*\d/.test(text) ? `Number\n${text}` : text], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = b.base_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Unable to download the file');
  }
}

const PAGE_SIZES = [50, 100, 500, 1000];

const EMPTY_STATE: any = {
  numbers: [],
  total: 0,
  expectedTotal: 0,
  pages: 1,
  source: null,
  baseName: null,
  baseId: null,
  warning: null,
  error: null,
  bases: [],
};

const selectClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground';

const NumbersViewerModal = ({ show, onHide, campaignId, campaignName, expectedTotal, initialBaseId = '' }) => {
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(false);
  const [lineInput, setLineInput] = useState('');
  const [highlightLine, setHighlightLine] = useState(null);
  const [baseId, setBaseId] = useState(initialBaseId ? String(initialBaseId) : '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [downloading, setDownloading] = useState(false);
  const rowRefs = useRef({} as any);

  const { numbers, warning, error, source, baseName, total, pages } = state;
  const offset = (page - 1) * pageSize;

  useEffect(() => {
    if (!show) {
      setBaseId(initialBaseId ? String(initialBaseId) : '');
      setPage(1);
      setHighlightLine(null);
      setLineInput('');
      setState(EMPTY_STATE);
    }
  }, [show, initialBaseId]);

  useEffect(() => {
    if (!show || !campaignId) return;
    setLoading(true);
    const url = API_VOICE_CAMPAIGNS_NUMBERS.replace(':id', campaignId);
    getApiCall(url, { ...(baseId ? { base_id: baseId } : {}), page, page_size: pageSize })
      .then((res: any) => {
        setState({
          bases: Array.isArray(res?.bases) ? res.bases : [],
          numbers: Array.isArray(res?.numbers) ? res.numbers : [],
          total: Number(res?.total) || 0,
          expectedTotal: Number(res?.expected_total) || 0,
          pages: Number(res?.pages) || 1,
          source: res?.source || null,
          baseName: res?.base_name || null,
          baseId: res?.base_id ?? null,
          warning: res?.warning || null,
          error: null,
        });
        if (res?.page && Number(res.page) !== page) setPage(Number(res.page));
      })
      .catch((err: any) =>
        setState({
          ...EMPTY_STATE,
          error:
            typeof err === 'string'
              ? err
              : err?.message || 'Unable to load numbers for this campaign.',
        }),
      )
      .finally(() => setLoading(false));
  }, [show, campaignId, baseId, page, pageSize]);

  useEffect(() => {
    if (loading || highlightLine == null) return;
    rowRefs.current[highlightLine]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loading, highlightLine, numbers]);

  const goToLine = () => {
    const n = parseInt(lineInput, 10);
    if (!n || n < 1 || n > total) return;
    setHighlightLine(n);
    setPage(Math.ceil(n / pageSize));
  };

  const changeBase = (value) => {
    setBaseId(value);
    setPage(1);
    setHighlightLine(null);
  };

  const changePageSize = (value) => {
    setPageSize(Number(value));
    setPage(1);
    setHighlightLine(null);
  };

  const downloadBase = () => {
    const id = baseId || (state.baseId != null ? String(state.baseId) : '');
    const stored = state.bases.find((b) => String(b.base_id) === id && b.download_url);
    setDownloading(true);
    if (stored) {
      downloadBaseFile(stored, campaignId).finally(() => setDownloading(false));
      return;
    }
    getApiCall(API_VOICE_CAMPAIGNS_NUMBERS.replace(':id', campaignId), id ? { base_id: id } : {})
      .then((res: any) => {
        const all = Array.isArray(res?.numbers) ? res.numbers : [];
        const name = String(res?.base_name || `campaign_${campaignId}_numbers`).replace(/\.csv$/i, '');
        downloadCsv([['Number'], ...all.map((n) => [n])], `${name}.csv`);
        if (res?.truncated) toast.warning(`Downloaded the first ${all.length.toLocaleString('en-IN')} numbers only.`);
      })
      .catch(() => {})
      .finally(() => setDownloading(false));
  };

  const claimedTotal = Number(state.expectedTotal) || Number(expectedTotal) || 0;
  const perBase = state.bases.length > 0;
  const showMismatch = !loading && !error && !perBase && claimedTotal > 0 && total === 0;
  const selectedBaseId = baseId || (state.baseId != null ? String(state.baseId) : '');

  const sourceLabel =
    source === 'contact_groups'
      ? 'Contact group'
      : source === 'base_temp_file'
        ? 'Uploaded file (unprocessed)'
        : baseName || (source === 'base_file' ? 'Base file' : null);

  return (
    <Dialog open={!!show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Base File Viewer
            {(campaignId || campaignName) && (
              <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                {campaignName ? `${campaignName} ` : ''}
                {campaignId ? `(ID: ${campaignId})` : ''} — view only
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {!loading && error && (
          <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && !error && (warning || showMismatch) && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div className="space-y-1">
              {warning && <div>{warning}</div>}
              {showMismatch && (
                <div>
                  This campaign reports {claimedTotal} number{claimedTotal === 1 ? '' : 's'}, but
                  none could be read.
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && sourceLabel && total > 0 && (
          <div className="mb-2 text-xs text-muted-foreground">Source: {sourceLabel}</div>
        )}

        <div className="mb-3 flex flex-wrap gap-2">
          {perBase && (
            <select
              className={`${selectClass} min-w-0 flex-1`}
              value={selectedBaseId}
              onChange={(e) => changeBase(e.target.value)}
              aria-label="Base"
            >
              {state.bases.map((b) => (
                <option key={b.base_id} value={String(b.base_id)}>
                  {b.base_name} ({Number(b.total_numbers || 0).toLocaleString('en-IN')})
                </option>
              ))}
            </select>
          )}
          <div className="flex flex-1 gap-2">
            <Input
              type="number"
              placeholder="Line number"
              value={lineInput}
              min={1}
              max={total || undefined}
              onChange={(e) => setLineInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && goToLine()}
            />
            <Button onClick={goToLine} disabled={!total}>
              Go
            </Button>
          </div>
        </div>

        <div className="max-h-[340px] overflow-y-auto rounded-md border border-border">
          <div className="sticky top-0 z-10 flex border-b border-border bg-muted px-3 py-2 text-sm font-semibold">
            <span className="w-16 flex-none">#</span>
            <span>Number</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : numbers.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No numbers found</div>
          ) : (
            numbers.map((num, idx) => {
              const line = offset + idx + 1;
              const hit = line === highlightLine;
              return (
                <div
                  key={line}
                  ref={(el) => (rowRefs.current[line] = el)}
                  className={`flex items-center border-b border-border px-3 py-2 text-sm ${
                    hit ? 'bg-amber-100 font-semibold text-amber-950 dark:bg-amber-500/25 dark:text-amber-100' : ''
                  }`}
                >
                  <span className="w-16 flex-none">{line}.</span>
                  <span>{num}</span>
                </div>
              );
            })
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <label className="flex items-center gap-2">
            Rows per page
            <select className={selectClass} value={pageSize} onChange={(e) => changePageSize(e.target.value)}>
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            <span>
              Page {page} of {pages}
            </span>
            <Button variant="outline" size="icon" disabled={loading || page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" disabled={loading || page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DialogFooter className="!justify-between">
          <span className="text-sm text-muted-foreground">
            Total lines: {total.toLocaleString('en-IN')}
            {!perBase && claimedTotal > 0 && claimedTotal !== total
              ? ` (campaign total: ${claimedTotal.toLocaleString('en-IN')})`
              : ''}
          </span>
          <div className="flex gap-2">
            {canDownloadReports() && (
              <Button variant="outline" onClick={downloadBase} disabled={loading || downloading || !total}>
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download base
              </Button>
            )}
            <Button variant="outline" onClick={onHide}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NumbersViewerModal;
